import React, { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { userService } from "@/services";
import { formatEmail, isValidEmail } from "@/utils";
import { useUser, useLoading } from "@/contexts";
import { GoogleIcon, MicrosoftIcon, PhoneIcon } from "@/svgs";

const LoginScreen: React.FC = () => {
  const navigate = useNavigate();
  const { user, loading, refreshUser } = useUser();
  const { withLoading } = useLoading();

  const [formData, setFormData] = useState({ email: "", password: "" });
  const [touched, setTouched] = useState({ email: false, password: false });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState<string | null>(null);
  const [submitMessage, setSubmitMessage] = useState<string | null>(null);
  const [showPassword, setShowPassword] = useState(false);
  const [showResendButton, setShowResendButton] = useState(false);
  const [isResendingVerification, setIsResendingVerification] = useState(false);

  const errors = useMemo(() => {
    const emailValue = formatEmail(formData.email);

    const emailError =
      emailValue.length === 0
        ? "Informe seu e-mail."
        : isValidEmail(emailValue)
          ? null
          : "E-mail inválido (ex: nome@dominio.com).";

    const passwordError =
      formData.password.length === 0 ? "Informe sua senha." : null;

    return { email: emailError, password: passwordError };
  }, [formData.email, formData.password]);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    let sanitizedValue = value;

    if (name === "email") {
      sanitizedValue = formatEmail(value);
    } else if (name === "password") {
      sanitizedValue = value.replace(/^\s+|\s+$/g, "");
    }

    setFormData((prev) => ({
      ...prev,
      [name]: sanitizedValue,
    }));

    setSubmitError(null);
    setSubmitMessage(null);
    setShowResendButton(false);
  };

  const handleBlur = (e: React.FocusEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    if (name === "email") {
      const cleaned = formatEmail(value);
      setFormData((prev) => ({ ...prev, email: cleaned }));
    }
    if (name === "password") {
      setFormData((prev) => ({ ...prev, password: value.trim() }));
    }

    if (name === "email" || name === "password") {
      setTouched((prev) => ({ ...prev, [name]: true }));
    }
  };

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();

    const sanitizedEmail = formatEmail(formData.email || "");
    const sanitizedPassword = (formData.password || "").trim();

    setFormData({ email: sanitizedEmail, password: sanitizedPassword });
    setTouched({ email: true, password: true });
    setSubmitError(null);
    setSubmitMessage(null);
    setShowResendButton(false);

    if (!sanitizedEmail || !sanitizedPassword) {
      setSubmitError("Preencha e-mail e senha corretamente.");
      return;
    }

    if (errors.email || errors.password) return;

    setIsSubmitting(true);

    try {
      const result = await withLoading(() =>
        userService.login({
          email: sanitizedEmail,
          password: sanitizedPassword,
        }),
      );

      if (!result.success && result.error.title === "E-mail não verificado") {
        setSubmitError(
          "E-mail não verificado. Verifique sua caixa de entrada.",
        );
        setShowResendButton(true);
        return;
      }

      if (result.success) {
        await refreshUser();
        navigate("/inicio");
      } else {
        setSubmitError(result.error.detail ?? result.error.title);
      }
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleGoogleLogin = async () => {
    setSubmitError(null);
    setSubmitMessage(null);
    setIsSubmitting(true);

    try {
      const result = await withLoading(() => userService.loginWithGoogle());

      if (result.success) {
        if (result.isNewUser) {
          navigate("/completar-cadastro", {
            state: {
              uid: result.user.uid,
              email: result.user.email,
              provider: "google",
            },
          });
        } else {
          await refreshUser();
          navigate("/inicio");
        }
      } else {
        const isPopupClosed =
          result.error?.detail?.includes("popup-closed-by-user") ||
          result.error?.title?.includes("popup-closed-by-user");

        if (isPopupClosed) {
          setSubmitError(null);
        } else {
          setSubmitError(
            result.error?.detail ?? "Erro ao autenticar com o Google.",
          );
        }
      }
    } catch (error: any) {
      console.error("Erro capturado no bloco do Google Login:", error);
      if (error?.code !== "auth/popup-closed-by-user") {
        setSubmitError("Ocorreu um erro ao abrir o login do Google.");
      }
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleMicrosoftLogin = async () => {
    setIsSubmitting(true);
    try {
      const result = await withLoading(() => userService.loginWithMicrosoft());
      if (result.success) {
        if (result.isNewUser) {
          navigate("/completar-cadastro", {
            state: {
              uid: result.user.uid,
              email: result.user.email,
              provider: "microsoft",
            },
          });
        } else {
          await refreshUser();
          navigate("/inicio");
        }
      } else {
        setSubmitError(result.error.detail ?? result.error.title);
      }
    } catch (error) {
      setSubmitError("Erro ao autenticar com a Microsoft.");
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <section className="stats">
      <div className="container">
        <div className="center auth-modal-card">
          <div className="auth-logo">
            <img
              src="/mimocupido-logo.png"
              alt="Mimo Cupido"
              className="auth-logo-icon"
            />
          </div>

          <div className="auth-social-wrapper">
            <button
              type="button"
              className="auth-social-btn google"
              onClick={handleGoogleLogin}
              disabled={isSubmitting}
            >
              <GoogleIcon />
              Continuar com o Google
            </button>

            <button
              type="button"
              className="auth-social-btn microsoft"
              onClick={handleMicrosoftLogin}
              disabled={isSubmitting}
            >
              <MicrosoftIcon />
              Continuar com a Microsoft
            </button>
          </div>

          <div className="auth-modal-separator">
            <span>ou</span>
          </div>

          <form className="auth-modal-form" onSubmit={handleSubmit}>
            <div className="auth-modal-input-group">
              <input
                name="email"
                type="email"
                value={formData.email}
                onChange={handleChange}
                onBlur={handleBlur}
                placeholder="E-mail"
                autoComplete="username"
                inputMode="email"
              />
            </div>

            <div className="auth-modal-input-group">
              <div className="auth-modal-password-wrapper">
                <input
                  name="password"
                  type={showPassword ? "text" : "password"}
                  value={formData.password}
                  onChange={handleChange}
                  onBlur={handleBlur}
                  placeholder="Senha"
                  autoComplete="current-password"
                />
                <button
                  type="button"
                  className="auth-modal-password-toggle"
                  onClick={() => setShowPassword(!showPassword)}
                >
                  {showPassword ? "Ocultar" : "Mostrar"}
                </button>
              </div>
            </div>

            <button
              className="auth-modal-submit-btn"
              type="submit"
              disabled={
                isSubmitting ||
                Boolean(errors.email) ||
                Boolean(errors.password)
              }
            >
              {isSubmitting ? "Entrando..." : "Entrar"}
            </button>

            <div className="auth-modal-footer-links">
              <a
                href="/esqueci-senha"
                onClick={(e) => {
                  e.preventDefault();
                  navigate("/esqueci-senha");
                }}
              >
                Redefinir Senha
              </a>
            </div>

            <div className="auth-modal-footer-links">
              <span className="auth-modal-footer-text">Não tem conta?</span>

              <a
                href="/cadastro"
                onClick={(e) => {
                  e.preventDefault();
                  navigate("/cadastro");
                }}
              >
                Crie uma
              </a>
            </div>

            {submitError ? (
              <div className="auth-modal-error">{submitError}</div>
            ) : null}

            {submitMessage ? (
              <div className="auth-modal-message">{submitMessage}</div>
            ) : null}
          </form>
        </div>
      </div>
    </section>
  );
};

export default LoginScreen;
